import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import client from "../api/client";

/**
 * Auth is cookie-based. Identity is the single source of truth returned by
 * GET /auth/me — the SPA never decodes a JWT. On mount we probe /auth/me; the
 * result populates { user, organization, role }. login()/logout()/refresh()
 * mutate that state.
 */
const AuthContext = createContext(null);

// Normalise the various success envelopes into { user, organization, role }.
function normalize(body) {
  if (!body) return { user: null, organization: null, role: null };
  const payload = body.data ?? body; // success shape is { data, meta }
  const user = payload.user ?? payload ?? null;
  const organization = payload.organization ?? user?.organization ?? null;
  const role = payload.role ?? user?.role ?? null;
  return { user: user || null, organization: organization || null, role: role || null };
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);

  const apply = useCallback((body) => {
    const next = normalize(body);
    setUser(next.user);
    setOrganization(next.organization);
    setRole(next.role);
    return next;
  }, []);

  const clear = useCallback(() => {
    setUser(null);
    setOrganization(null);
    setRole(null);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const res = await client.get("/auth/me");
      return apply(res.data);
    } catch (err) {
      clear();
      return { user: null, organization: null, role: null };
    }
  }, [apply, clear]);

  useEffect(() => {
    let active = true;
    (async () => {
      await refresh();
      if (active) setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [refresh]);

  // Throws on failure so callers (LoginPage) can inspect err.response.status
  // (403 => awaiting approval / suspended).
  const login = useCallback(
    async (email, password) => {
      const res = await client.post("/auth/login", { email, password });
      // /auth/me is canonical; fall back to the login payload if it 401s.
      const me = await refresh();
      if (me.user) return me;
      return apply(res.data);
    },
    [refresh, apply]
  );

  const logout = useCallback(async () => {
    try {
      await client.post("/auth/logout");
    } catch (err) {
      /* clear locally regardless */
    }
    clear();
  }, [clear]);

  const value = useMemo(
    () => ({ user, organization, role, loading, login, logout, refresh }),
    [user, organization, role, loading, login, logout, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an <AuthProvider>");
  }
  return ctx;
};

export default AuthContext;
